// ============================================================
//  ACTIVITY TIMELINE — ADMIN DASHBOARD
//  Path: src/components/ActivityTimeline.tsx
//
//  NEW FILE. Shows recorded actions (backend TaskEvent) as a timeline:
//  WHEN (date + time to the second), WHAT happened (arrived, job
//  started, done, cancelled, no-show reported, dispute resolved, …),
//  WHO did it (name + role), and for location actions WHERE: the
//  distance to the address it was checked against, whether it was
//  verified (within the arrival radius), the coordinates, and a map link.
//
//  Used in three places:
//    - TasksPage task details          → one task, oldest first
//    - CustomerDetailPage "Activity"   → all of one customer's tasks
//    - ExpertDetailPage "Activity"     → all of one tasker's jobs
//
//  <TimelineSection> loads the data itself (loading / error / retry);
//  <ActivityTimeline> only renders a list it's given.
// ============================================================

import React, { useEffect, useMemo, useState } from 'react'
import { TimelineEvent } from '../types'

const EVENT_INFO: Record<string, { icon: string; label: string; group: 'location' | 'problem' | 'other' }> = {
  hired:                     { icon: '🤝', label: 'Tasker hired',                         group: 'other' },
  schedule_confirmed:        { icon: '📅', label: 'Time confirmed',                       group: 'other' },
  on_way:                    { icon: '🚗', label: 'On the way',                           group: 'location' },
  arrived:                   { icon: '📍', label: 'Arrived',                              group: 'location' },
  on_way_to_pickup:          { icon: '🚗', label: 'On the way to pickup',                 group: 'location' },
  arrived_at_pickup:         { icon: '🏪', label: 'Arrived at pickup',                    group: 'location' },
  picked_up:                 { icon: '📦', label: 'Picked up',                            group: 'location' },
  job_started:               { icon: '▶️', label: 'Job started',                          group: 'location' },
  customer_arrived:          { icon: '🙋', label: 'Customer arrived (confirmed by tasker)', group: 'location' },
  customer_on_way:           { icon: '🚗', label: 'Customer on the way',                  group: 'location' },
  customer_checked_in:       { icon: '📍', label: 'Customer checked in',                  group: 'location' },
  marked_done:               { icon: '✅', label: 'Marked as done',                       group: 'other' },
  completion_confirmed:      { icon: '👍', label: 'Completion confirmed',                 group: 'other' },
  cancelled:                 { icon: '✖️', label: 'Cancelled',                            group: 'problem' },
  no_show_reported:          { icon: '🚫', label: 'Tasker no-show reported',              group: 'problem' },
  customer_no_show_reported: { icon: '🚫', label: 'Customer no-show reported',            group: 'problem' },
  dispute_opened:            { icon: '⚠️', label: 'Dispute opened',                       group: 'problem' },
  dispute_resolved:          { icon: '⚖️', label: 'Dispute resolved by admin',            group: 'problem' },
  auto_settled:              { icon: '🤖', label: 'Auto-settled after 24 h',              group: 'problem' },
  auto_settlement_review:    { icon: '🔎', label: 'Flagged for review (24 h)',            group: 'problem' },
  start_reminder_sent:       { icon: '🔔', label: 'Start reminder sent to the tasker',    group: 'other' },
  arrival_nudge_sent:        { icon: '🔔', label: '"You\'re at the address" reminder sent', group: 'location' },
}

const ROLE_LABELS: Record<string, string> = {
  customer: 'Customer', expert: 'Tasker', worker: 'Company worker', system: 'System', admin: 'Admin',
}

const TARGET_LABELS: Record<string, string> = { job: 'the address', pickup: 'the pickup' }

const OUTCOME_LABELS: Record<string, string> = {
  tasker_no_show:   'Tasker no-show — card hold released / refunded, tasker suspended',
  customer_no_show: 'Customer no-show — card charged, tasker paid',
  admin_review:     'Sent to admin review',
  no_attendance:    'Nobody checked in — card hold released / refunded',
  pay_expert:       'Tasker paid (card charged)',
  refund_customer:  'Customer not charged / refunded (hold released)',
}

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' })

const fmtDistance = (m: number | null | undefined) =>
  m == null || !Number.isFinite(m) ? '' : m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`

const LocationLine: React.FC<{ e: TimelineEvent }> = ({ e }) => {
  const hasGps = e.latitude != null && e.longitude != null
  const where  = e.target ? TARGET_LABELS[e.target] || e.target : null
  let badge: React.ReactNode = null
  if (where && e.verified === true) {
    badge = <span className="text-green-400">✓ Verified — {fmtDistance(e.distanceMeters)} from {where}</span>
  } else if (where && e.verified === false) {
    badge = <span className="text-red-400">✗ Not verified — {fmtDistance(e.distanceMeters)} from {where}</span>
  } else if (where && !hasGps) {
    badge = <span className="text-yellow-400">⚠ No location sent — not verified</span>
  } else if (where && hasGps) {
    badge = <span className="text-gray-400">Location recorded (address has no map point to compare)</span>
  } else if (hasGps) {
    badge = <span className="text-gray-400">Location recorded</span>
  }
  if (!badge) return null
  return (
    <p className="text-xs mt-1">
      {badge}
      {hasGps && (
        <span className="text-gray-600"> · {e.latitude!.toFixed(5)}, {e.longitude!.toFixed(5)}</span>
      )}
      {e.mapUrl && (
        <a href={e.mapUrl} target="_blank" rel="noopener noreferrer" className="ml-2 text-brand-400 hover:underline">View on map</a>
      )}
    </p>
  )
}

const MetaLine: React.FC<{ e: TimelineEvent }> = ({ e }) => {
  const m = e.metadata || {}
  const parts: string[] = []
  if (m.recheck) parts.push('Re-check-in')
  if (m.onBehalfOf) parts.push("on the tasker's behalf")
  if (m.step) parts.push(`for "${m.step.replace(/_/g, ' ')}"`)
  if (m.note) parts.push(`note: “${m.note}”`)
  if (m.outcome && OUTCOME_LABELS[m.outcome]) parts.push(OUTCOME_LABELS[m.outcome])
  if (m.resolution && OUTCOME_LABELS[m.resolution]) parts.push(OUTCOME_LABELS[m.resolution])
  if (m.by) parts.push(`by the ${m.by}`)
  if (m.suspensionHours) parts.push(`suspended ${m.suspensionHours} h`)
  if (m.refundPercent != null) parts.push(`${m.refundPercent}% refund`)
  if (m.refundedAmount != null && Number(m.refundedAmount) > 0) parts.push(`$${Number(m.refundedAmount).toFixed(2)} refunded`)
  if (m.reason) parts.push(`“${m.reason}”`)
  if (parts.length === 0) return null
  return <p className="text-xs text-gray-400 mt-1">{parts.join(' · ')}</p>
}

type Filter = 'all' | 'location' | 'problem'

export const ActivityTimeline: React.FC<{
  events:      TimelineEvent[]
  showTask?:   boolean                      // show which task each event belongs to
  onOpenTask?: (taskId: string) => void
}> = ({ events, showTask = false, onOpenTask }) => {
  const [filter, setFilter] = useState<Filter>('all')
  const shown = useMemo(
    () => events.filter((e) => filter === 'all' || (EVENT_INFO[e.event]?.group ?? 'other') === filter),
    [events, filter]
  )

  if (events.length === 0) {
    return <p className="text-sm text-gray-500">No recorded activity yet.</p>
  }

  return (
    <div>
      <div className="flex gap-2 mb-3">
        {([['all', 'All'], ['location', 'Arrivals & location'], ['problem', 'Problems & decisions']] as [Filter, string][]).map(([f, label]) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1 text-xs rounded-lg transition-colors ${filter === f ? 'bg-brand-600 text-white' : 'bg-surface-tertiary text-gray-400 hover:text-white'}`}>
            {label}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <p className="text-sm text-gray-500">Nothing in this filter.</p>
      ) : (
        <ol className="relative border-l border-border-light ml-2">
          {shown.map((e) => {
            const info = EVENT_INFO[e.event] || { icon: '•', label: e.event.replace(/_/g, ' '), group: 'other' }
            return (
              <li key={e.id} className="ml-4 mb-4">
                <span className="absolute -left-2.5 flex items-center justify-center w-5 h-5 rounded-full bg-surface-tertiary text-[11px]">{info.icon}</span>
                <p className="text-xs text-gray-500">{fmtDateTime(e.createdAt)}</p>
                <p className="text-sm text-white font-medium">{info.label}</p>
                <p className="text-xs text-gray-400">
                  {ROLE_LABELS[e.actorRole] || e.actorRole}{e.actorName && e.actorRole !== 'system' ? ` — ${e.actorName}` : ''}
                  {showTask && e.taskTitle && (
                    <>
                      {' · '}
                      {onOpenTask ? (
                        <button onClick={() => onOpenTask(e.taskId)} className="text-brand-400 hover:underline">{e.taskTitle}</button>
                      ) : (
                        <span className="text-gray-300">{e.taskTitle}</span>
                      )}
                    </>
                  )}
                </p>
                <LocationLine e={e} />
                <MetaLine e={e} />
              </li>
            )
          })}
        </ol>
      )}
    </div>
  )
}

// Loads and shows a timeline. `fetcher` is one of the admin.service
// timeline calls; change `reloadKey` to load again (e.g. after a
// dispute is resolved).
export const TimelineSection: React.FC<{
  fetcher:     () => Promise<any>
  title?:      string
  showTask?:   boolean
  onOpenTask?: (taskId: string) => void
  reloadKey?:  string | number
}> = ({ fetcher, title, showTask, onOpenTask, reloadKey }) => {
  const [events, setEvents]   = useState<TimelineEvent[] | null>(null)
  const [error, setError]     = useState('')
  const [loading, setLoading] = useState(true)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    fetcher()
      .then((res) => { if (!cancelled) setEvents(res?.data?.data?.events ?? []) })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load timeline:', (err as any)?.response?.status, (err as any)?.message)
        setError(err?.response?.data?.message || (err?.isNetworkError
          ? 'Could not reach the server — check your connection and try again.'
          : 'Could not load the activity timeline.'))
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey, attempt])

  return (
    <div className="mb-4">
      {title && <p className="text-xs text-gray-500 uppercase mb-2">{title}</p>}
      {loading ? (
        <p className="text-sm text-gray-400">Loading activity…</p>
      ) : error ? (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center justify-between gap-2">
          <p className="text-red-400 text-xs">{error}</p>
          <button onClick={() => setAttempt((a) => a + 1)}
            className="px-2 py-1 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light shrink-0">Try again</button>
        </div>
      ) : (
        <ActivityTimeline events={events || []} showTask={showTask} onOpenTask={onOpenTask} />
      )}
    </div>
  )
}

export default ActivityTimeline